import { type TFunction } from "i18next";
import { type useTime } from "utils";

import { type Athlete } from "src/athlete/CoachAthleteList";
import { type StatusBadgeTone } from "src/components/StatusBadge";
import {
  getProductSubscriptionDisplayStatus,
  PRODUCT_SUBSCRIPTION_STATUS_TONES,
  type ProductSubscriptionDisplayStatus,
} from "src/helpers/productSubscriptionStatus";

export type AthleteProduct = Athlete["products"][number];

type AthleteProductStatus = ProductSubscriptionDisplayStatus | "ONE_TIME";

function getAthleteProductStatus(
  product: AthleteProduct,
): AthleteProductStatus {
  if (product.subscriptionStatus === null) {
    return "ONE_TIME";
  }

  return getProductSubscriptionDisplayStatus({
    status: product.subscriptionStatus,
    cancelAt: product.cancelAt,
  });
}

export function getAthleteProductTone(
  product: AthleteProduct,
): StatusBadgeTone {
  const status = getAthleteProductStatus(product);

  return status === "ONE_TIME"
    ? "slate"
    : PRODUCT_SUBSCRIPTION_STATUS_TONES[status];
}

export function getAthleteProductStatusLabel(
  t: TFunction,
  time: Pick<ReturnType<typeof useTime>, "formatInstantDate">,
  product: AthleteProduct,
): string {
  const status = getAthleteProductStatus(product);

  if (status === "ONE_TIME" && product.accessEndsAt) {
    return t("athletesPage.productOneTimeUntil", {
      date: time.formatInstantDate(product.accessEndsAt, "shortDate"),
    });
  }

  if (status === "ONE_TIME") {
    return t("athletesPage.productOneTime");
  }

  const statusText =
    status === "CANCEL_SCHEDULED" && product.cancelAt
      ? t("productSubscriptions.endsOn", {
          date: time.formatInstantDate(product.cancelAt),
        })
      : t(`productSubscriptions.statuses.${status}`);

  return t("athletesPage.productSubscriptionStatus", { status: statusText });
}
